import { useState } from 'react'
import { LuUserRoundCog } from 'react-icons/lu'
import camelcaseKeys from 'camelcase-keys'
import Button from '@mui/material/Button'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Typography from '@mui/material/Typography'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Alert from '@mui/material/Alert'
import Stack from '@mui/material/Stack'
import Modal from './Modal'
import { callTool } from '../lib/tools'
import { userTextWithEmail } from '../utilities/userLabel'
import {
  memberLabel,
  nodeTransferArgs,
  nodeTransferRefusal,
  rootDelegationLines,
  transferCandidates,
  transferChangedOwner,
  transferEmail,
  type NodeTransferResult,
  type TransferMember,
} from '../utilities/nodeTransfer'

type Props = {
  nodeId: string
  nodeName: string
  /** `nodes.owner_user_id`: left out of the suggestions. */
  ownerUserId: string | null | undefined
  /** The node's tenant (`nodes.owner_org_id`), whose members the node can go to. */
  orgId: string | null | undefined
}

/**
 * "Give to another member": node ownership transfer (app#376, router `node_transfer`, router#1771;
 * docs/PERMISSIONS.md § 5 Node). Shown only to the node's controllers (its owner, or an org
 * Owner/Admin); the router asks the same rule.
 *
 * ⚠⚠ THE RESULT IS SHOWN, NOT JUST THE SUCCESS. It lists every root delegation the new owner now
 * answers for (§ 5 Node: "so none is taken over silently"), and says when the node's local MCP was
 * turned off by the change of owner (§ 8).
 *
 * ⚠⚠ THE NEW OWNER IS NAMED BY EMAIL (§ 5 Node, D-16). A Member who owns a node cannot list the
 * org's members (router#1765), so the dialog never depends on the list: the reader types an address.
 * When the list can be read (an org Owner or Admin), its members are offered as suggestions.
 * ⚠ The list is read here, from `org_members_list`, and not from the shared members store: that
 * store keeps the LAST org it read when a read fails, which would offer the members of another org.
 */
export default function NodeTransfer({ nodeId, nodeName, ownerUserId, orgId }: Readonly<Props>) {
  const [open, setOpen] = useState(false)
  const [members, setMembers] = useState<TransferMember[]>([]) // empty: not read, or refused
  const [typed, setTyped] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<{ to: string; transfer: NodeTransferResult } | null>(null)

  const onOpen = async () => {
    setOpen(true)
    setError(null)
    setResult(null)
    setTyped('')
    setMembers([])
    if (!orgId) return
    // ⚠ Only suggestions: a refusal (a Member, router#1765) leaves the list empty, and the reader
    // types the address.
    try {
      const res = await callTool<{ members?: Record<string, unknown>[] }>('org_members_list', { org_id: orgId })
      setMembers((res.members ?? []).map(m => camelcaseKeys(m)) as unknown as TransferMember[])
    } catch (e) {
      console.warn('org_members_list failed:', e)
      setMembers([])
    }
  }

  const candidates = transferCandidates(members, ownerUserId)
  const email = transferEmail(typed)

  const onTransfer = async () => {
    if (!email) return
    setBusy(true)
    setError(null)
    try {
      const transfer = await callTool<NodeTransferResult>('node_transfer', nodeTransferArgs(nodeId, email))
      const known = candidates.find(m => m.email.toLowerCase() === email.toLowerCase())
      setResult({ to: known ? memberLabel(known) : email, transfer })
    } catch (e) {
      console.error('node_transfer failed:', e)
      setError(nodeTransferRefusal(e))
    } finally {
      setBusy(false)
    }
  }

  const delegations = result ? rootDelegationLines(result.transfer.root_delegations) : []

  return (
    <>
      <Button
        variant="outlined"
        startIcon={<LuUserRoundCog />}
        size="small"
        sx={{ marginRight: 1 }}
        onClick={() => void onOpen()}
      >
        Give to another member
      </Button>
      <Modal open={open} onClose={() => setOpen(false)} maxWidth="sm" color="primary.main">
        <DialogTitle variant="subtitle1" color="primary">
          Give {nodeName} to another member
        </DialogTitle>
        <DialogContent>
          {result && !transferChangedOwner(result.transfer) ? (
            <Alert severity="info">{nodeName} already belongs to {result.to}. Nothing was changed.</Alert>
          ) : result ? (
            <Stack spacing={2}>
              <Alert severity="success">
                {nodeName} now belongs to {result.to}. They control it from now on: who else may use it, its services and
                its compute. The node keeps the people who may use it.
              </Alert>
              {result.transfer.local_mcp_turned_off && (
                <Alert severity="info">
                  The node’s local MCP is now off, because its owner changed. An owner or admin of the organization can turn
                  it on again.
                </Alert>
              )}
              {delegations.length ? (
                <Alert severity="warning">
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    {result.to} now answers for these ways the node gives root:
                  </Typography>
                  {delegations.map(line => (
                    <Typography key={line} variant="body2" component="div">
                      • {line}
                    </Typography>
                  ))}
                </Alert>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Nothing on this node gives root to anyone else.
                </Typography>
              )}
            </Stack>
          ) : (
            <Stack spacing={2}>
              <Typography variant="body2">
                The new owner controls the node: they decide who else may use it, and they manage its services and compute.
                Its name, networks, services and the people who may use it do not change. The node’s local MCP turns off.
                You keep what an access list or your role in the organization gives you.
              </Typography>
              <Autocomplete
                freeSolo
                size="small"
                options={candidates.map(m => m.email).filter(Boolean)}
                // ⚠ An option is the address itself, so a picked suggestion puts the address in the box.
                renderOption={({ key, ...props }, option) => {
                  const m = candidates.find(c => c.email === option)
                  return (
                    <li key={key} {...props}>
                      {m ? userTextWithEmail(m, option) : option}
                    </li>
                  )
                }}
                inputValue={typed}
                onInputChange={(_e, value) => setTyped(value)}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Email address of the new owner"
                    helperText="The new owner must be a member of this node’s organization."
                  />
                )}
              />
            </Stack>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {result ? (
            <Button onClick={() => setOpen(false)}>Close</Button>
          ) : (
            <>
              <Button onClick={() => setOpen(false)}>Cancel</Button>
              <Button variant="contained" disabled={!email || busy} onClick={() => void onTransfer()}>
                Give the node
              </Button>
            </>
          )}
        </DialogActions>
      </Modal>
    </>
  )
}
